import { type ChatFastAction } from "@do/shared-types";
import { type ButtonProps, Button } from "@do/ui/components/button";
import { cn } from "@do/ui/lib/utils";
import { useMemo } from "react";
import { ButtonStyles } from "../../../../constants";
import { useOptionalChat } from "../../../../providers/chat-provider";
import { ButtonIcon } from "./button-icon";
import { createMessageButtonClickHandler } from "./fast-action";

export type MessageButtonProps = ButtonProps & {
  buttonType: "store" | "url" | "tel" | "code" | "payload";
  id: string;
  "data-test": string;
  "data-button-metadata": string;
  isBackButton: boolean;
  button: ChatFastAction;
  template: {
    iconName?: string;
    iconUrl?: string;
    subLabel?: string;
    style?: ButtonStyles;
  };
};

const getButtonClassName = (buttonType: ButtonStyles, subLabel: boolean) => {
  switch (buttonType) {
    case ButtonStyles.CallToAction:
      return cn(
        "bg-(--message-button-cta-background)/(--message-button-cta-background-opacity)",
        "hover:bg-(--message-button-cta-hover-background)/(--message-button-cta-hover-background-opacity)",
        "text-(--message-button-cta-foreground)/(--message-button-cta-foreground-opacity)",
        "hover:text-(--message-button-cta-hover-foreground)/(--message-button-cta-hover-foreground-opacity)",
        "border-(length:--message-button-cta-border-width)",
        "border-(--message-button-cta-border-color)/(--message-button-cta-border-opacity)",
        "hover:border-(--message-button-cta-hover-border-color)/(--message-button-cta-hover-border-opacity)",
        "rounded-(--message-button-cta-border-radius)",
        "font-(--message-button-cta-font-weight)! font-(family-name:--message-button-cta-font-family)",
        "text-(length:--message-button-cta-font-size)",
        "gap-(--message-button-cta-gap)",
        "[box-shadow:var(--message-button-cta-box-shadow)]",
        subLabel
          ? "min-h-(--message-button-cta-height-with-sub-label)"
          : "min-h-(--message-button-cta-height)"
      );
    default:
      return cn(
        "bg-(--message-button-background)/(--message-button-background-opacity)",
        "hover:bg-(--message-button-hover-background)/(--message-button-hover-background-opacity)",
        "text-(--message-button-foreground)/(--message-button-foreground-opacity)",
        "hover:text-(--message-button-hover-foreground)/(--message-button-hover-foreground-opacity)",
        "border-(length:--message-button-border-width)",
        "border-(--message-button-border-color)/(--message-button-border-opacity)",
        "hover:border-(--message-button-hover-border-color)/(--message-button-hover-border-opacity)",
        "rounded-(--message-button-border-radius)",
        "font-(--message-button-font-weight)! font-(family-name:--message-button-font-family)",
        "text-(length:--message-button-font-size)",
        "gap-(--message-button-gap)",
        "[box-shadow:var(--message-button-box-shadow)]",
        subLabel
          ? "min-h-(--message-button-height-with-sub-label)"
          : "min-h-(--message-button-height)"
      );
  }
};

export const MessageButton = ({
  button,
  buttonType,
  className,
  isBackButton,
  template,
  id,
  ...props
}: MessageButtonProps) => {
  const chat = useOptionalChat();

  const title = useMemo(() => button.title || "", [button.title]);

  const subLabel = useMemo(() => template.subLabel || undefined, [template.subLabel]);

  const handleClick = createMessageButtonClickHandler(chat, button, {
    id,
    buttonType,
    isBackButton,
  });

  return (
    <Button
      id={`message-${button.id}-button`}
      onClick={handleClick}
      className={cn(
        "group/message-button",
        "has-[>img]:pl-3 has-[>img]:pr-3.5 has-[>[data-message-button-icon-image]]:pl-3 has-[>[data-message-button-icon-image]]:pr-3.5 has-[>svg]:pr-3.5",
        "!whitespace-normal break-words shrink min-w-0 w-auto max-w-full h-auto",
        getButtonClassName(template.style as ButtonStyles, !!subLabel),
        className
      )}
      {...props}
    >
      <ButtonIcon
        isBackButton={isBackButton}
        buttonType={buttonType}
        template={template}
        buttonId={button.id}
      />

      <span
        className={cn(
          "message-button-content flex flex-col items-start text-left min-w-0 w-full",
          "whitespace-normal break-words"
        )}
        id={`message-${button.id}-button-content`}
      >
        <span
          id={`message-${button.id}-button-title`}
          className="leading-none whitespace-normal break-words"
        >
          {title}
        </span>

        {subLabel && (
          <span
            id={`message-${button.id}-button-sub-label`}
            className={cn(
              "mt-[3px]",
              template.style === ButtonStyles.CallToAction
                ? "text-(--message-button-cta-sub-label-color)/(--message-button-cta-sub-label-color-opacity) text-(length:--message-button-cta-sub-label-font-size)"
                : "text-(--message-button-sub-label-color)/(--message-button-sub-label-color-opacity) text-(length:--message-button-sub-label-font-size)",
              "leading-none whitespace-normal break-words"
            )}
          >
            {template.subLabel}
          </span>
        )}
      </span>
    </Button>
  );
};
